import { Box, HStack } from '@chakra-ui/react'
import { ColumnDef } from '@tanstack/react-table'
import { toast } from 'react-toastify'

import { Buttons } from '@/components/shared/buttons'
import { Table } from '@/components/shared/displayers/Table'
import { Can } from '@/features/sessao/providers/auth-context'
import { useAuthStore } from '@/features/sessao/store/useAuthStore'
import { CallProduct } from '@/services/backend/config/entities'
import { Repository } from '@/services/repository'
import { NUMBER_TO_CURRENCY } from '@/utils/helpers/Currency'

type Props = {
  data: {
    callProducts: CallProduct[]
    isEditable: boolean
  }
  onChange: (res: CallProduct) => void
}

export default function Component({
  data: { callProducts, isEditable },
  onChange,
}: Props) {
  const { user } = useAuthStore()

  const onChangeQuantity = async (action: 'Add' | 'Remove', id: string) => {
    try {
      const res = await Repository.Entity.CallProduct[action]({ id })
      onChange(res)
    } catch (error: any) {
      toast.error(error.message)
    }
  }

  const columns: ColumnDef<(typeof callProducts)[0]>[] = [
    {
      header: 'Produto',
      accessorKey: 'product',
      sortingFn: (a, b) =>
        (a.original.productName ?? '') > (b.original.productName ?? '')
          ? 1
          : -1,
      cell: ({ row }) => {
        return (
          <Box>
            <Box fontWeight="bold" fontSize="xs">
              {row.original.productSku}
            </Box>
            <Box fontSize="sm">{row.original.productName}</Box>
          </Box>
        )
      },
    },
    {
      header: 'Qtd.',
      accessorKey: 'quantity',
    },
  ]

  if (user) {
    const hiddenColumns: ColumnDef<(typeof callProducts)[0]>[] = [
      {
        header: 'R$ Unit.',
        accessorKey: 'unitCost',
        cell: ({ row }) => (
          <Box textAlign="right">
            {NUMBER_TO_CURRENCY(row.original.unitCost ?? 0)}
          </Box>
        ),
      },
      {
        header: 'R$ Total',
        accessorKey: 'totalCost',
        cell: ({ row }) => {
          const total = (row.original.unitCost ?? 0) * row.original.quantity
          return <Box textAlign="right">{NUMBER_TO_CURRENCY(total)}</Box>
        },
      },
      {
        header: '',
        accessorKey: 'actions',
        enableSorting: false,
        cell: ({ row }) =>
          isEditable ? (
            <Can asDefault I="update" a="CallItem">
              <HStack justifyContent="flex-end">
                <Buttons.Add
                  onClick={() => onChangeQuantity('Add', row.original.id)}
                />
                <Buttons.Remove
                  onClick={() => onChangeQuantity('Remove', row.original.id)}
                />
              </HStack>
            </Can>
          ) : null,
      },
    ]

    columns.push(...hiddenColumns)
  }

  return (
    <Table
      data={callProducts}
      columns={columns}
      initialSorting={[{ id: 'product', desc: false }]}
    />
  )
}
